/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, {
  ReactElement,
  Dispatch,
  SetStateAction,
  Fragment,
  useState,
  useEffect,
} from 'react';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/selection-mode/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/highlight/lib/styles/index.css';

import { Worker, Viewer } from '@react-pdf-viewer/core';
import {
  defaultLayoutPlugin,
  ToolbarSlot,
} from '@react-pdf-viewer/default-layout';
import {
  SelectionMode,
  selectionModePlugin,
} from '@react-pdf-viewer/selection-mode';
import { zoomPlugin } from '@react-pdf-viewer/zoom';

import { useIntl } from 'react-intl';
import { Layout } from 'antd';

import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { DbFile, DbModuleFile } from '~src/types/db';
import IconButton from '~components/_common/IconButton/IconButton';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';

interface PdfViewerProps {
  fileUrl: string;
  canBeClosed?: boolean;
}

export const PdfViewerCopy: React.FC<PdfViewerProps> = ({
  fileUrl,
  canBeClosed = true,
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const { setFileToView } = useFileViewerContext();
  const [currentZoom, setCurrentZoom] = useState(1);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (isFocused) {
      zoomTo(currentZoom);
    }
  }, [currentZoom]);

  const selectionModePluginInstance = selectionModePlugin({
    selectionMode: SelectionMode.Hand,
  });

  const { SwitchSelectionMode } = selectionModePluginInstance;

  const zoomPluginInstance = zoomPlugin({ enableShortcuts: true });
  const { ZoomIn, Zoom, ZoomOut, zoomTo } = zoomPluginInstance;

  useEffect(() => {
    const onCtrlScroll = (e: any) => {
      if (e.ctrlKey) {
        e.stopImmediatePropagation();
        e.stopPropagation();
        e.preventDefault();
        if (e.deltaY > 0) {
          setCurrentZoom((prevState) => prevState - 0.1);
        } else if (e.deltaY < 0) {
          setCurrentZoom((prevState) => prevState + 0.1);
        }
      }
    };
    window.addEventListener('mousewheel', onCtrlScroll, { passive: false });
    return () => {
      window.removeEventListener('mousewheel', onCtrlScroll);
    };
  }, [currentZoom]);

  const renderToolbar = (Toolbar: (props: any) => ReactElement) => {
    return (
      <div style={{ display: 'flex' }}>
        {canBeClosed && (
          <IconButton
            click={() => setFileToView(undefined)}
            iconStyle="far"
            icon="times"
            customStyle={styles.closeBtn}
            tooltip={intl.formatMessage({ id: 'close' })}
            backgroundOff
          />
        )}

        <Toolbar selectionModePluginInstance={selectionModePluginInstance}>
          {(slots: ToolbarSlot) => {
            const {
              GoToNextPage,
              GoToPreviousPage,
              CurrentPageInput,
              Download,
              Print,
              EnterFullScreen,
              Open,
            } = slots;
            return (
              <div
                className="rpv-core__viewer"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  width: '100%',
                  justifyContent: 'space-between',
                }}>
                <GoToNextPage />
                <CurrentPageInput />
                <GoToPreviousPage />
                <ZoomIn></ZoomIn>
                <Zoom />
                <ZoomOut />
                <SwitchSelectionMode mode={SelectionMode.Hand} />
                <SwitchSelectionMode mode={SelectionMode.Text} />
                <EnterFullScreen />
                <Open />
                <Download />
                <Print />
              </div>
            );
          }}
        </Toolbar>
      </div>
    );
  };

  const defaultLayoutPluginInstance = defaultLayoutPlugin({ renderToolbar });

  return (
    <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js">
      <Viewer
        fileUrl={fileUrl}
        plugins={[
          defaultLayoutPluginInstance,
          selectionModePluginInstance,
          zoomPluginInstance,
        ]}
        renderPage={(props) => (
          <div
            onMouseOver={() => {
              setIsFocused(true);
            }}
            onMouseLeave={() => {
              setIsFocused(false);
            }}>
            <Layout>
              {props.canvasLayer.children}
              <div style={{ userSelect: 'text' }}>
                {props.textLayer.children}
              </div>
              {props.annotationLayer.children}
            </Layout>
          </div>
        )}
        renderError={(err) => {
          return (
            <Fragment>
              <IconButton
                click={() => canBeClosed && setFileToView(undefined)}
                iconStyle="far"
                icon="times"
                customStyle={styles.closeBtn}
                tooltip={intl.formatMessage({ id: 'close' })}
                backgroundOff
              />
              <div css={styles.errorMessageContainer}>
                <p css={styles.errorMessage}>
                  {intl.formatMessage({ id: 'opened-pdf-viewer-error' })}
                </p>
              </div>
            </Fragment>
          );
        }}
      />
    </Worker>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  closeBtn: {
    color: theme.colors.darkGray,
    fontSize: '20px',
    transition: '0.3s',
    cursor: 'pointer',
  },
  errorMessageContainer: {
    display: 'flex',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorMessage: {
    color: theme.colors.red,
    fontSize: 24,
    width: '50%',
    textAlign: 'center',
    border: `1px solid ${theme.colors.red}`,
    backgroundColor: theme.colors.white,
    borderRadius: 9,
    padding: '20px 20px',
    boxShadow: '0px 0px 19px rgba(0, 0, 0, 0.07)',
  },
});
